import { useCallback, useEffect, useState } from 'react';
import { api, type AcctCorp, type MisuCancelKind, type MisuCancelRow } from '../lib/api';
import { ErrorBox, dash, money } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import { useSticky } from '../lib/sticky';

/**
 * 회계 › 발행 요청 › 「취소」·「수정」 칩 — 영업이 올린 청구 취소·수정 요청을 발행 요청과 같은 화면에서 처리(2026-10-19 way 회의 녹음
 * 「세금 계산서 발행 요청에 취소도 같이 뜨게끔 · 발행 현황도 보고 취소 현황도 보고 수정 현황도」). 서버 `functions/src/misuCancel.ts`.
 *
 * - 승인 = 서버가 한 번에: 발행 계산서 수정(마이너스) 발행 · 발행 요청 닫기/다시 열기 · 미수 청구상태 초기화(취소) / 청구대상(수정).
 * - 반려 = 사유 필수 → 영업에게 결과 팝업.
 * - 위칸 법인 필터는 발행 요청 표와 같은 값(`acct.invreq.corp`)을 쓴다 — 법인 = 계산서 법인, 없으면 접수 청구법인.
 * 미수금 › 「취소·수정」 보기(`SettleMisuCancel`)는 같은 목록을 결과 조회로만 쓴다.
 */

const at = (v: unknown) => String(v ?? '').slice(0, 16).replace('T', ' ');
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
/** 배지·팝업 다시 맞추기 — `InvoiceRequest.pokeInvoiceRequests` 와 같은 사건(서로 import 하지 않으려고 직접 쏜다). */
const pokeInvoiceRequests = () => window.dispatchEvent(new Event('invreq:poke'));

export function KindChip({ kind }: { kind: MisuCancelKind | string }) {
  const cls = kind === '수정' ? 'bg-violet-100 text-violet-800' : 'bg-red-100 text-red-700';
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{kind}</span>;
}

export function CancelStatusChip({ s, kind }: { s: string; kind: string }) {
  const label = s === '대기' ? '처리 대기' : s === '승인' ? (kind === '취소' ? '취소 완료' : '수정 승인') : s === '반려' ? '반려' : '요청 거둠';
  const cls = s === '대기' ? 'bg-amber-100 text-amber-800' : s === '승인' ? 'bg-emerald-100 text-emerald-700'
    : s === '반려' ? 'bg-red-50 text-red-700' : 'bg-canvas text-ink-faint';
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>;
}

/** 「100만 → 150만」 한 줄. 취소면 지금 금액만. */
export function amountLine(r: Pick<MisuCancelRow, 'kind' | 'old_total' | 'fix_total' | 'fix_vat_included'> & { last_request?: MisuCancelRow['last_request'] }): string {
  // 10-19 이전 요청은 요청 때 합계(old_total)가 없다 — 마지막 발행 요청 합계로 보여 준다.
  const old = r.old_total ?? r.last_request?.total ?? null;
  if (r.kind !== '수정') return old ? `${money(old)}원` : '—';
  return `${old ? money(old) : '—'} → ${money(r.fix_total)}원`;
}

/** 목록 — 구분(취소·수정) × 상태(진행 중·끝난 것·전체) × 법인. `openId` 가 오면(팝업·챗 링크) 그 건 창을 연다. */
export function CancelRequestPanel({
  kind, state, q, corps, onOpen, reloadKey = 0, toolbar,
}: {
  kind: MisuCancelKind | null; state: 'active' | 'done' | 'all'; q: string; corps: AcctCorp[];
  /** 줄을 누르면 — 창은 부르는 쪽이 연다(팝업·챗 링크 `?mcr=` 와 같은 창). */
  onOpen: (id: number, row: MisuCancelRow) => void; reloadKey?: number; toolbar?: React.ReactNode;
}) {
  const [corpId, setCorpId] = useSticky<number | null>('acct.invreq.corp', null);
  const [rows, setRows] = useState<MisuCancelRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(() => {
    api.listMisuCancelRequests({ status: state === 'done' ? '끝남' : state === 'all' ? '전체' : '대기', kind, corpId, q: q.trim() || undefined })
      .then((r) => { setRows(r.rows); setErr(null); }).catch((e) => setErr(errText(e)));
  }, [state, kind, corpId, q]);
  useEffect(load, [load, reloadKey]);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        {toolbar}
        <select className="field w-40" value={corpId ?? ''} onChange={(e) => setCorpId(e.target.value ? Number(e.target.value) : null)} title="청구법인(계산서 법인 · 없으면 접수 청구법인)">
          <option value="">법인 전체</option>
          {corps.filter((c) => c.invoice_code).map((c) => <option key={c.corp_id} value={c.corp_id}>{c.short_name}({c.invoice_code})</option>)}
        </select>
      </div>
      {err && <ErrorBox message={err} />}
      {rows === null ? <div className="card p-4 text-sm text-ink-soft">불러오는 중…</div>
        : rows.length === 0 ? <p className="text-sm text-ink-faint">{state === 'active' ? '처리를 기다리는 요청이 없습니다.' : '요청이 없습니다.'}</p>
        : (
          <div className="card max-h-[calc(100vh-14rem)] overflow-auto">
            <table className="w-full min-w-[64rem] text-sm">
              <thead className="[&_th]:sticky [&_th]:top-0 [&_th]:bg-surface"><tr>
                <th className="th">#</th><th className="th">구분</th><th className="th">처리</th><th className="th">청구법인</th><th className="th">접수·정산월</th>
                <th className="th">거래처</th><th className="th">계산서</th><th className="th text-right">금액</th><th className="th">사유 · 수정 내용</th>
                <th className="th">요청</th><th className="th">처음 확인 · 처리</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={String(r.request_id)} className={`cursor-pointer hover:bg-canvas ${r.status === '대기' && !r.seen_at ? 'bg-red-50/60' : ''}`} onDoubleClick={() => onOpen(Number(r.request_id), r)}>
                    <td className="td">{String(r.request_id)}</td>
                    <td className="td"><KindChip kind={r.kind} /></td>
                    <td className="td"><CancelStatusChip s={r.status} kind={r.kind} /></td>
                    <td className="td whitespace-nowrap">{dash(r.corp_name)}</td>
                    <td className="td whitespace-nowrap">{r.recv_id} · {r.drive_mon}</td>
                    <td className="td font-medium">{dash(r.cust_name)}</td>
                    <td className="td whitespace-nowrap text-xs">
                      {r.invoices.length === 0 ? <span className="text-ink-faint">없음</span> : r.invoices.map((t) => (
                        <span key={t.id} className={`mr-1 ${t.amended ? 'text-ink-faint line-through' : t.issuer ? 'text-emerald-700' : ''}`}
                              title={t.amended ? '수정발행으로 취소됨' : t.issuer ? `${t.issuer} 발행 ${t.issue_date ?? ''}` : '발행 전'}>
                          {t.no}{t.deposit_linked && !t.amended ? ' 💰' : ''}
                        </span>
                      ))}
                    </td>
                    <td className="td num whitespace-nowrap">{amountLine(r)}</td>
                    <td className="td max-w-[20rem] text-xs">
                      <div className="truncate" title={r.reason}>{r.reason}</div>
                      {r.kind === '수정' && r.fix_note && <div className="truncate text-violet-800" title={r.fix_note}>수정: {r.fix_note}</div>}
                    </td>
                    <td className="td whitespace-nowrap text-xs">{dash(r.requested_name ?? r.requested_by)} · {at(r.requested_at).slice(5)}</td>
                    <td className="td whitespace-nowrap text-xs text-ink-soft">
                      {r.decided_at ? `${dash(r.decided_name)} · ${at(r.decided_at).slice(5)}` : r.seen_name ? `${r.seen_name} 확인 중` : <span className="font-semibold text-red-600">아무도 안 봄</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

/** 한 건 — 회계 입력이면 열 때 「처음 확인」을 남기고 승인·반려. 조회만이면 보기. */
export function CancelRequestDetail({ id, initial, onClose, onDone, readOnly = false }: {
  id: number; initial?: MisuCancelRow | null; onClose: () => void; onDone?: () => void; readOnly?: boolean;
}) {
  const { me } = useMe();
  const canDecide = !readOnly && canScreen(me, 'accounting', 'write');
  const [r, setR] = useState<MisuCancelRow | null>(initial ?? null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    // 회계 입력 = 「처음 확인」 기록 + 최신 값 · 조회만 = 목록 값 그대로(없으면 목록에서 찾아 온다).
    const run = canDecide
      ? api.seeMisuCancel({ requestId: id }).then((x) => x.row)
      : api.listMisuCancelRequests({ status: '전체' }).then((x) => x.rows.find((y) => Number(y.request_id) === id) ?? null);
    run.then((row) => { if (alive && row) setR(row); if (alive && !row) setErr('요청을 찾을 수 없습니다.'); pokeInvoiceRequests(); })
      .catch((e) => { if (alive) setErr(errText(e)); });
    return () => { alive = false; };
  }, [id, canDecide]);

  const pending = r?.status === '대기';
  const live = (r?.invoices ?? []).filter((t) => !t.amended && Number(t.total) > 0);
  const linked = live.filter((t) => t.deposit_linked);

  async function decide(approve: boolean) {
    if (!r) return;
    if (!approve && !note.trim()) { setErr('반려 사유를 적어 주세요 — 버스인접수담당자에게 팝업으로 보입니다.'); return; }
    if (approve) {
      const issued = live.filter((t) => t.issuer).map((t) => t.no);
      const lines = r.kind === '취소'
        ? [`청구를 취소합니다(${r.cust_name ?? ''} · 접수 ${r.recv_id} · ${r.drive_mon}).`,
            issued.length ? `· 계산서 ${issued.join(', ')} 를 수정(마이너스) 발행합니다${r.fix_written_date ? `(작성일자 ${r.fix_written_date})` : ''}.` : '· 발행된 계산서는 없습니다.',
            '· 걸린 발행 요청을 닫고 청구상태를 청구 요청 전으로 돌립니다(입금상태·입금일은 그대로).',
            '· 버스인접수담당자에게 「같은 접수번호로 다시 청구 요청」 안내가 갑니다.']
        : [`청구 금액을 ${amountLine(r)} 으로 수정합니다.`,
            issued.length ? `· 계산서 ${issued.join(', ')} 를 수정(마이너스) 발행합니다${r.fix_written_date ? `(작성일자 ${r.fix_written_date})` : ''}.` : '· 발행된 계산서는 없습니다.',
            `· 발행 요청을 바뀐 금액${r.fix_written_date ? `·작성일자 ${r.fix_written_date}` : ''}으로 다시 엽니다 — 승인 뒤 그 요청에서 「발행대장에 올리기」로 새로 발행하세요.`];
      if (!window.confirm(`${lines.join('\n')}\n\n승인할까요?`)) return;
    }
    setBusy(true); setErr(null);
    try {
      const out = await api.decideMisuCancel({ requestId: id, approve, note: note.trim() || undefined });
      setDone(!approve ? '반려했습니다 — 버스인접수담당자에게 사유가 팝업으로 갑니다.'
        : r.kind === '취소' ? `취소를 마쳤습니다${out.amendIds.length ? ` — 수정발행 ${out.amendIds.length}장` : ''}. 버스인접수담당자가 같은 접수번호로 다시 청구 요청합니다.`
          : `수정을 승인했습니다 — 발행 요청 #${out.reopenRequestId ?? ''} 이(가) 바뀐 금액으로 다시 열렸습니다. 「발행 요청」 칩에서 발행대장에 올려 주세요.`);
      onDone?.();
      pokeInvoiceRequests();
      const fresh = await api.listMisuCancelRequests({ status: '전체' }).then((x) => x.rows.find((y) => Number(y.request_id) === id) ?? null).catch(() => null);
      if (fresh) setR(fresh);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  }

  return (
    <div className="card space-y-3 p-5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-bold">청구 {r?.kind ?? ''} 요청 #{id}</h2>
        {r && <KindChip kind={r.kind} />}
        {r && <CancelStatusChip s={r.status} kind={r.kind} />}
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={busy}>닫기</button>
        {r && pending && canDecide && !done && (
          <>
            <button className="btn-ghost text-red-700" onClick={() => decide(false)} disabled={busy}>반려</button>
            <button className="btn-primary" onClick={() => decide(true)} disabled={busy || linked.length > 0}
              title={linked.length ? '입금이 연결된 계산서가 있어 먼저 입금 내역에서 연결을 풀어야 합니다' : undefined}>
              {busy ? '처리 중…' : r.kind === '취소' ? '승인(계산서 취소·청구 초기화)' : '승인(수정 발행)'}
            </button>
          </>
        )}
      </div>
      {!r && !err && <div className="text-ink-soft">불러오는 중…</div>}
      {err && <ErrorBox message={err} />}
      {done && <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800">{done}</div>}
      {r && (
        <>
          <dl className="grid grid-cols-[6.5rem_1fr] gap-y-1 sm:grid-cols-[6.5rem_1fr_6.5rem_1fr]">
            <dt className="text-ink-soft">거래처</dt><dd className="font-semibold">{dash(r.cust_name)}</dd>
            <dt className="text-ink-soft">접수·정산월</dt><dd>{r.recv_id} · {r.drive_mon}</dd>
            <dt className="text-ink-soft">청구법인</dt><dd>{dash(r.corp_name)}</dd>
            <dt className="text-ink-soft">지금 상태</dt>
            <dd>{dash(r.cur_req_status)}{r.cur_bank_status ? ` · ${r.cur_bank_status}` : ''}{r.cur_deposit_date ? ` · 입금일 ${r.cur_deposit_date}` : ''}</dd>
            <dt className="text-ink-soft">요청</dt><dd>{dash(r.requested_name ?? r.requested_by)} · {at(r.requested_at)}</dd>
            <dt className="text-ink-soft">요청 때 상태</dt><dd>{dash(r.status_at_request)}{r.bank_at_request ? ` · ${r.bank_at_request}` : ''}</dd>
            <dt className="text-ink-soft">처음 확인</dt><dd>{r.seen_name ? `${r.seen_name} · ${at(r.seen_at)}` : '—'}</dd>
            <dt className="text-ink-soft">발행 요청</dt>
            <dd>{r.last_request ? `#${r.last_request.id} · ${money(r.last_request.total)}원${r.last_request.issue_way ? ` · ${r.last_request.issue_way}` : ''}` : '—'}</dd>
          </dl>
          <div className={`rounded-lg border px-3 py-2 ${r.kind === '수정' ? 'border-violet-200 bg-violet-50' : 'border-red-200 bg-red-50'}`}>
            <div className="text-xs font-semibold text-ink-soft">{r.kind === '수정' ? '수정 내용' : '취소 — 청구를 통째로 돌려보냅니다'}</div>
            {r.kind === '수정' && (
              <div className="mt-1">
                <b className="text-base">{amountLine(r)}</b>
                <span className="ml-2 text-xs text-ink-soft">
                  ({r.fix_vat_included ? '부가세 포함으로 입력' : '부가세 별도로 입력'} · 공급가 {money(r.fix_supply)} + 세액 {money(r.fix_vat)})
                </span>
                <div className="mt-1 whitespace-pre-wrap">{r.fix_note}</div>
              </div>
            )}
            <div className="mt-1"><span className="text-xs text-ink-soft">세금계산서 발행일자 </span>
              {r.fix_written_date ? <b>{r.fix_written_date}</b> : <span className="text-ink-faint">— (옛 요청 · 승인한 날로 수정 발행)</span>}</div>
            <div className="mt-1 whitespace-pre-wrap"><span className="text-xs text-ink-soft">사유 </span>{r.reason}</div>
          </div>
          <div>
            <div className="mb-1 text-xs font-semibold text-ink-soft">이 접수·월 세금계산서</div>
            {r.invoices.length === 0 ? <div className="text-xs text-ink-faint">발행대장에 계산서가 없습니다(카드·현금 청구 또는 발행 전).</div> : (
              <ul className="space-y-0.5 text-xs">
                {r.invoices.map((t) => (
                  <li key={t.id} className={t.amended ? 'text-ink-faint line-through' : ''}>
                    {t.no} · {money(t.total)}원 · {t.amended ? '수정발행으로 취소됨' : t.issuer ? `${t.issuer} 발행 ${t.issue_date ?? ''}` : '발행 전(승인하면 내립니다)'}
                    {t.deposit_linked && !t.amended && <span className="ml-1 font-semibold text-red-700">입금 연결됨 — 입금 내역에서 연결을 먼저 푸세요</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {r.decided_at && (
            <div className="text-xs text-ink-soft">
              처리: {dash(r.decided_name ?? r.decided_by)} · {at(r.decided_at)}{r.decide_note ? ` — ${r.decide_note}` : ''}
              {r.status === '승인' && r.kind === '수정' && r.reopen_request_id ? ` · 다시 연 발행 요청 #${r.reopen_request_id}` : ''}
            </div>
          )}
          {pending && canDecide && !done && (
            <label className="block">
              <span className="mb-1 block text-xs font-medium">메모 · 반려 사유 <span className="font-normal text-ink-faint">반려는 사유 필수 — 버스인접수담당자 팝업에 그대로 보입니다</span></span>
              <textarea className="field min-h-[3.5rem]" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
          )}
          {pending && !canDecide && <p className="text-xs text-ink-faint">승인·반려는 회계 입력 권한이 필요합니다.</p>}
        </>
      )}
    </div>
  );
}
